import React, { FC, useCallback, useState } from "react";
import { CountPill } from "../../../videos/selectedVideoCardV2/panels";
import { MultiStepOrderModal } from "../../../../orders/MultiStepOrderModal";
import { useUser } from "../../../../../hooks/useUser";
import { isInternal } from "../../../../../utils";
import { SECTION_TITLE_CLASS, SECTION_TITLE_STYLE } from "../components/SectionCard";
import { SECTION_IDS, SECTION_SCROLL_MARGIN } from "../components/SectionRail";
import { SegmentedTabs, tabId } from "../components/SegmentedTabs";
import type { SegmentedTab } from "../components/SegmentedTabs";
import { ProductVideoList } from "./ProductVideoList";
import { ProductStories } from "./ProductStories";

/**
 * Section 3, Videos. The list itself is today's (VideoSection via
 * ProductVideoList, a copy of productDetail/ProductVideos.tsx: sort, filters,
 * Load more, the Non-project checkbox, the grid and the inline `?video=`
 * open). Stories is ProductStories: ProductEmbeds' data in the video details
 * page's story design (StoryTile grid, dashed empty / error wells).
 *
 * The old Videos | Stories Switcher (adminVideosV2/switcher.tsx, shared with
 * the brand's Videos page, so not edited) is replaced here by a section
 * heading like the other sections' (16px title + CountPill) and, for
 * brand-side users only, Videos | Stories as the page's SegmentedTabs (the
 * ViewToggle look; arrow keys; 44px below md), with the list under it as
 * their tabpanel. The Switcher hid Stories from internal roles
 * (switcher.tsx:54); so does this, and without tabs the list is no tabpanel.
 *
 * The count pill shows the total of the list that is showing — videos, or
 * stories on the Stories tab — once it's known and above 0 (the video list
 * reports 0 while loading, and an empty list has its own empty state), and
 * hides while a video is open inline (the list query is off then). One count
 * serves both: only one list is mounted at a time, and a tab switch clears it.
 *
 * The order modal behind the list's empty-state button (Request a quote /
 * New order — its only opener, VideoSection.tsx:487-495) lives here, not in
 * the list, which remounts on every Stories → Videos switch: its hooks fetch
 * on mount, open or not (content types; SOWs with a brand; the RMN brand's
 * brands; Slack channels for internal roles — useOrderFormData.ts,
 * MultiStepOrderModal.tsx:259-327). It mounts the first time the list view
 * shows (the Videos tab, no video open: on a plain load, in the same commit
 * as the list, as before) and then stays mounted (its close animation, its
 * reset-on-open effect), so a tab switch doesn't fetch again. It can't wait for the empty state itself: the list reports 0 from
 * its first commit, before its first load lands (VideoSection.tsx:172-174,
 * useVideosQuery.ts:29), so "reported 0" can't tell loading from empty.
 *
 * `showVideosPanel` is held by the page, as today's page held it
 * (productDetail/index.tsx:23), because the page needs it too: the
 * `?video=` view (header + this section only) applies while the block is
 * actually showing the video, not while Stories is.
 */

interface Props {
  /** The route's brand, as today (`<ProductEmbeds {...{ brandId }} />`). */
  brandId: number;
  showVideosPanel: boolean;
  onShowVideosPanel: (show: boolean) => void;
  /** A video is open inline: the page's `videoOpen` (`showVideosPanel` and a `?video=` id, index.tsx). */
  videoOpen: boolean;
  /** The page's floor while it holds the section in place after a tab switch (index.tsx); null = none. */
  minHeight?: number | null;
}

const TAB_PREFIX = "product-videos";
const PANEL_ID = "product-videos-panel";
const TABS: SegmentedTab[] = [
  { id: "videos", label: "Videos", panelId: PANEL_ID },
  { id: "stories", label: "Stories", panelId: PANEL_ID },
];

export const VideosSection: FC<Props> = ({ brandId, showVideosPanel, onShowVideosPanel, videoOpen, minHeight }) => {
  const { role } = useUser();
  // The showing list's total; its setter goes to both lists as is (stable).
  const [count, setCount] = useState<number | null>(null);
  // Where the list draws its "Non-project only" toggle (VideoSection's
  // nonProjectToggleTarget): in this row, left of Videos | Stories, instead of
  // a row of its own. Empty (and hidden) whenever the list doesn't show it.
  const [toggleSlot, setToggleSlot] = useState<HTMLDivElement | null>(null);

  // The order modal: open, and mounted yet (see the top).
  const [orderOpen, setOrderOpen] = useState(false);
  const openOrder = useCallback(() => setOrderOpen(true), []);
  const closeOrder = useCallback(() => setOrderOpen(false), []);
  const [orderMounted, setOrderMounted] = useState(false);
  if (!orderMounted && showVideosPanel && !videoOpen) setOrderMounted(true);

  const showTabs = !isInternal(role);
  const selectedTab = showVideosPanel ? "videos" : "stories";
  const showCount = !videoOpen && count != null && count > 0;

  // Each list remounts when its tab is picked and reports its total only once
  // loaded; clearing the count here keeps an old total from flashing in
  // between. SegmentedTabs calls onSelect for the selected tab too.
  const selectTab = (stories: boolean) => {
    if (stories === !showVideosPanel) return;
    setCount(null);
    onShowVideosPanel(!stories);
  };

  return (
    <section
      id={SECTION_IDS.videos}
      aria-label="Videos"
      className={`${SECTION_SCROLL_MARGIN} min-w-0`}
      style={minHeight ? { minHeight } : undefined}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className={SECTION_TITLE_CLASS} style={SECTION_TITLE_STYLE}>
            Videos
          </h2>
          {showCount && <CountPill value={count as number} />}
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-2">
          <div ref={setToggleSlot} className="flex items-center empty:hidden" />
          {showTabs && (
            <SegmentedTabs
              idPrefix={TAB_PREFIX}
              ariaLabel="Show videos or stories"
              items={TABS}
              selectedId={selectedTab}
              onSelect={(id) => selectTab(id === "stories")}
            />
          )}
        </div>
      </div>
      <div
        className="mt-4"
        role={showTabs ? "tabpanel" : undefined}
        id={showTabs ? PANEL_ID : undefined}
        aria-labelledby={showTabs ? tabId(TAB_PREFIX, selectedTab) : undefined}
      >
        {showVideosPanel ? (
          <ProductVideoList onOrder={openOrder} onCountChange={setCount} nonProjectToggleTarget={toggleSlot} />
        ) : (
          <ProductStories brandId={brandId} onCountChange={setCount} />
        )}
      </div>
      {/* Portalled: nothing in the section's DOM while it is closed. */}
      {orderMounted && <MultiStepOrderModal open={orderOpen} onClose={closeOrder} />}
    </section>
  );
};
